import { resolveEnemy } from '../../content/enemies/enemies';
import { getItemById } from '../../content/items/items';
import { getSkillById } from '../../content/skills/skills';
import { resolveInCombatFlee } from '../../domain/combat/flee';
import { resolvePlayerCombatTurn } from '../../domain/combat/resolveTurn';
import { useInventoryItem } from '../../domain/inventory/inventory';
import { getCurrentRoom } from '../../domain/dungeon/roomRules';
import { clearRoom } from '../../domain/events/applyConsequence';
import { modeFromRoom } from '../../domain/game/modeFromRoom';
import { grantExp } from '../../domain/progress/progression';
import { grantEnemyDrops } from '../../domain/rewards/enemyDrops';
import { createSaveSnapshot, writeSaveToStorage } from '../../domain/save/saveGame';
import { gameSignals } from '../../game/events/gameSignals';
import type { GameActions, GameStoreGet, GameStoreSet } from '../types';

export function createCombatActions(
  set: GameStoreSet,
  get: GameStoreGet,
): Pick<GameActions, 'finishCombat' | 'attemptFlee' | 'useSkill' | 'useItem'> {
  return {
    finishCombat: () => {
      const { dungeon, combat, player } = get();
      if (!combat || !player.alive || combat.playerDefeated) {
        return;
      }

      const won = combat.enemyHp <= 0;
      let nextFloor = dungeon;
      let nextPlayer = player;
      let message = 'Bạn rời trận — phòng chưa được giải phóng.';

      if (won) {
        nextFloor = clearRoom(dungeon, combat.sourceRoomId);
        const exp = grantExp(player, combat.expReward);
        const rewardedPlayer = { ...exp.player, gold: exp.player.gold + combat.goldReward };
        const dropResult = grantEnemyDrops(
          rewardedPlayer,
          resolveEnemy(combat.enemyId),
        );
        nextPlayer = dropResult.player;
        const dropMessages = dropResult.drops.map((drop) => {
          const name = getItemById(drop.itemId)?.name ?? drop.itemId;
          return `Nhặt ${name} ×${drop.quantity}.`;
        });
        message = [
          `Đã hạ ${combat.enemyName}.`,
          combat.goldReward > 0 ? `+${combat.goldReward} vàng.` : null,
          ...exp.messages,
          ...dropMessages,
          'Lối đi được giải phóng.',
        ]
          .filter(Boolean)
          .join(' ');
      }

      set({
        dungeon: nextFloor,
        player: nextPlayer,
        combat: null,
        mode: modeFromRoom(getCurrentRoom(nextFloor)),
        lastMessage: message,
      });
    },

    attemptFlee: () => {
      const { combat, player, dungeon } = get();
      if (!combat || !player.alive || combat.enemyHp <= 0 || combat.playerDefeated) {
        return;
      }

      const result = resolveInCombatFlee(player, combat, resolveEnemy(combat.enemyId));
      if (result.outcome === 'blocked') {
        set({ lastMessage: result.message });
      } else if (result.outcome === 'escaped') {
        const nextFloor = clearRoom(dungeon, combat.sourceRoomId);
        set({
          dungeon: nextFloor,
          combat: null,
          mode: modeFromRoom(getCurrentRoom(nextFloor)),
          lastMessage: result.message,
        });
      } else {
        set({
          player: result.player,
          combat: result.combat,
          mode: { type: 'combat', combatId: combat.id },
          lastMessage: result.message,
        });
      }
    },

    useSkill: (skillId) => {
      const { combat, player } = get();
      if (!combat || !player.alive || combat.playerDefeated || !player.skillIds.includes(skillId)) {
        return;
      }

      const skill = getSkillById(skillId);
      if (!skill) {
        return;
      }
      const result = resolvePlayerCombatTurn(player, combat, skill, resolveEnemy(combat.enemyId));
      if (!result.ok) {
        return;
      }

      set({
        player: result.player,
        combat: result.playerKilled
          ? { ...result.combat, playerDefeated: true }
          : result.combat,
        mode: { type: 'combat', combatId: combat.id },
        lastMessage: result.combat.log[0] ?? null,
      });

      if (result.attackMissed) {
        gameSignals.emit({ type: 'play-enemy-miss-fx' });
      }
      for (const damageEvent of result.damageEvents) {
        gameSignals.emit({
          type: 'play-enemy-hit-fx',
          damage: damageEvent.damage,
          source: damageEvent.source,
          element: damageEvent.element,
        });
      }
      if (result.enemyDefeated) {
        gameSignals.emit({ type: 'enemy-defeated', enemyId: combat.enemyId });
      }
      if (result.playerKilled) {
        gameSignals.emit({ type: 'player-defeated' });
      }
    },

    useItem: (itemId) => {
      const state = get();
      if (!state.player.alive) {
        return;
      }

      const inCombat = state.mode.type === 'combat' && state.combat !== null;
      const result = useInventoryItem(state.player, state.combat, itemId, inCombat);
      if (!result.ok) {
        set({ lastMessage: result.reason });
        return;
      }

      if (result.requestSave) {
        writeSaveToStorage(
          createSaveSnapshot({
            player: result.player,
            dungeon: state.dungeon,
            mode: state.mode,
            combat: result.combat,
            previousRoomId: state.previousRoomId,
            lastMessage: result.message,
          }),
        );
      }

      set({
        player: result.player,
        combat: result.combat,
        lastMessage: result.message,
        hasSave: result.requestSave ? true : state.hasSave,
      });
    },
  };
}
